import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  NotificationCategory,
  NotificationFilters as NotificationFilterState,
} from '@/notifications';
import { NotificationSearch } from './NotificationSearch';

interface NotificationFiltersProps {
  filters: NotificationFilterState;
  categories: NotificationCategory[];
  onChange: (updates: Partial<NotificationFilterState>) => void;
  onReset: () => void;
}

function normalizeCategories(
  categories: NotificationCategory[],
): NotificationCategory[] {
  const all = ['all' as const, ...categories];
  return Array.from(new Set(all));
}

export const NotificationFilters = memo(function NotificationFilters({
  filters,
  categories,
  onChange,
  onReset,
}: NotificationFiltersProps) {
  const { t } = useTranslation('entity-portal');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const availableCategories = useMemo(
    () => normalizeCategories(categories),
    [categories],
  );

  const fields = (
    <Stack spacing={1.5} direction={{ xs: 'column', lg: 'row' }}>
      <Box sx={{ flex: 2, minWidth: 220 }}>
        <NotificationSearch
          value={filters.search}
          onChange={(value) => onChange({ search: value })}
        />
      </Box>

      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel id="notification-filter-category-label">
          {t('notificationCenter.categoryLabel')}
        </InputLabel>
        <Select
          labelId="notification-filter-category-label"
          label={t('notificationCenter.categoryLabel')}
          value={filters.category}
          onChange={(event) =>
            onChange({ category: event.target.value as NotificationCategory })
          }
        >
          {availableCategories.map((category) => (
            <MenuItem key={category} value={category}>
              {t(`notificationCenter.category.${category}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="notification-filter-severity-label">
          {t('notificationCenter.severityLabel')}
        </InputLabel>
        <Select
          labelId="notification-filter-severity-label"
          label={t('notificationCenter.severityLabel')}
          value={filters.severity}
          onChange={(event) =>
            onChange({
              severity: event.target
                .value as NotificationFilterState['severity'],
            })
          }
        >
          <MenuItem value="all">
            {t('notificationCenter.severity.all')}
          </MenuItem>
          <MenuItem value="info">
            {t('notificationCenter.severity.info')}
          </MenuItem>
          <MenuItem value="success">
            {t('notificationCenter.severity.success')}
          </MenuItem>
          <MenuItem value="warning">
            {t('notificationCenter.severity.warning')}
          </MenuItem>
          <MenuItem value="critical">
            {t('notificationCenter.severity.critical')}
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="notification-filter-status-label">
          {t('notificationCenter.statusLabel')}
        </InputLabel>
        <Select
          labelId="notification-filter-status-label"
          label={t('notificationCenter.statusLabel')}
          value={filters.status}
          onChange={(event) =>
            onChange({
              status: event.target.value as NotificationFilterState['status'],
            })
          }
        >
          <MenuItem value="all">{t('notificationCenter.status.all')}</MenuItem>
          <MenuItem value="unread">
            {t('notificationCenter.status.unread')}
          </MenuItem>
          <MenuItem value="read">
            {t('notificationCenter.status.read')}
          </MenuItem>
          <MenuItem value="archived">
            {t('notificationCenter.status.archived')}
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }} disabled>
        <InputLabel id="notification-filter-date-label">
          {t('notificationCenter.dateRangeFuture')}
        </InputLabel>
        <Select
          labelId="notification-filter-date-label"
          label={t('notificationCenter.dateRangeFuture')}
          value="all"
        >
          <MenuItem value="all">
            {t('notificationCenter.futureControl')}
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }} disabled>
        <InputLabel id="notification-filter-product-label">
          {t('notificationCenter.productFuture')}
        </InputLabel>
        <Select
          labelId="notification-filter-product-label"
          label={t('notificationCenter.productFuture')}
          value="all"
        >
          <MenuItem value="all">
            {t('notificationCenter.futureControl')}
          </MenuItem>
        </Select>
      </FormControl>

      <Button variant="text" onClick={onReset}>
        {t('notificationCenter.resetFilters')}
      </Button>
    </Stack>
  );

  if (!isMobile) {
    return fields;
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={() => setMobileOpen(true)}
      >
        {t('notificationCenter.filters')}
      </Button>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      >
        <Box sx={{ width: 320, p: 2 }}>{fields}</Box>
      </Drawer>
    </>
  );
});
